import React, { useEffect, useCallback } from 'react'
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom'
import { useAuthState } from 'react-firebase-hooks/auth'

import { OError, OLoader } from '@edwin/react-web-admin'

import PSignIn from '@pages/PSignIn'
import PSignInWithEmailLink from '@pages/PSignInWithEmailLink'
import POrganizations from '@pages/POrganizations'
import POrganizationDetails from '@pages/POrganizationDetails'
import POrganizationTeam from '@pages/POrganizationTeam'
import PAdminUser from '@pages/PAdminUser'
import PAdminUsers from '@pages/PAdminUsers'
import PAddAdminUser from '@pages/PAddAdminUser'
import PUsers from '@pages/PUsers'
import PUser from '@pages/PUser'
import PAddOrganization from '@pages/PAddOrganization'
import PAddOrganizationTeam from '@pages/PAddOrganizationTeam'
import PAddTeamUser from '@pages/PAddTeamUser'
import PAssessments from '@pages/PAssessments'
import PChat from '@pages/PChat'
import useAuthClaims from '@hooks/useAuthClaims'
import { auth, updateAdminUserMetadata } from '@services/firebase'
import useStore from '@store/useStore'
import ROUTES from '@const/Routes'

//initialize
import '@services/firebase'
import '@services/axios'

const App = () => {
  const [claims, isAuthClaimsLoading, authClaimsError] = useAuthClaims()
  const [authUser, isAuthUserloading, authUserError] = useAuthState(auth)

  const {
    loadAdminUser,
    setAdminAccess,
    loadOrganizations,
    loadTeams,
    loadUsers,
    loadAdminUsers,
    loadAcl,
    isTeamsLoading,
    isOrganizationsLoading,
    isUsersLoading,
    isAdminUsersLoading,
    isAclLoading,
    isSuperAdmin,
    cleanStore,
    subscribeMobileDetectionListener,
    isMobile,
  } = useStore(state => ({
    loadAdminUser: state.loadAdminUser,
    setAdminAccess: state.setAdminAccess,
    loadOrganizations: state.loadOrganizations,
    loadTeams: state.loadTeams,
    loadUsers: state.loadUsers,
    loadAdminUsers: state.loadAdminUsers,
    loadAcl: state.loadAcl,
    isTeamsLoading: state.isTeamsLoading,
    isOrganizationsLoading: state.isOrganizationsLoading,
    isUsersLoading: state.isUsersLoading,
    isAdminUsersLoading: state.isAdminUsersLoading,
    isAclLoading: state.isAclLoading,
    isSuperAdmin: state.isSuperAdmin,
    cleanStore: state.cleanStore,
    subscribeMobileDetectionListener: state.subscribeMobileDetectionListener,
    isMobile: state.isMobile,
  }))

  const isCollectionsLoading =
    Boolean(
      isTeamsLoading ||
        isOrganizationsLoading ||
        isUsersLoading ||
        isAdminUsersLoading ||
        isAclLoading
    ) && authUser
  const isLoggedOut = !authUser && !isAuthUserloading
  const isLoading =
    isAuthUserloading || isAuthClaimsLoading || (authUser && !claims) || isCollectionsLoading
  const isError = authUserError || authClaimsError

  const getOrganizationsFromCustomClaims = useCallback(claims => {
    return Array.isArray(claims.acl) ? claims.acl : Object.keys(claims.acl || {})
  }, [])

  useEffect(() => {
    if (!isLoggedOut && claims) {
      const organizations = getOrganizationsFromCustomClaims(claims)
      const shouldFetchAll = claims.role === 'super_admin'
      loadAdminUser(authUser.uid)
      setAdminAccess(claims)
      loadOrganizations({ organizations, shouldFetchAll })
      loadTeams({ organizations, shouldFetchAll })
      loadUsers({ organizations, shouldFetchAll })
      loadAdminUsers(shouldFetchAll)
      loadAcl(shouldFetchAll)
    }
  }, [
    authUser?.uid,
    isLoggedOut,
    loadAdminUser,
    setAdminAccess,
    loadOrganizations,
    loadTeams,
    loadUsers,
    claims,
    loadAdminUsers,
    loadAcl,
    getOrganizationsFromCustomClaims,
  ])

  useEffect(() => {
    subscribeMobileDetectionListener()
  }, [subscribeMobileDetectionListener])

  useEffect(() => {
    if (isLoggedOut) {
      cleanStore()
    }
  }, [isLoggedOut, cleanStore, setAdminAccess, claims])

  useEffect(() => {
    if (authUser?.uid) {
      updateAdminUserMetadata(authUser.uid)
    }
  }, [authUser?.uid])

  if (isError) {
    return (
      <div className="h-screen flex items-center justify-center">
        <OError />
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <OLoader />
      </div>
    )
  }

  if (isLoggedOut) {
    return (
      <Router>
        <Routes>
          <Route exact path={ROUTES.SIGN_IN_WITH_EMAIL_LINK} element={<PSignInWithEmailLink />} />
          <Route path={ROUTES.ROOT} element={<PSignIn />} />
          <Route path="*" element={<Navigate replace to="/" />} />
        </Routes>
      </Router>
    )
  }

  // if (isMobile) {
  //   return (
  //     <Router>
  //       <Routes>
  //         <Route
  //           path={ROUTES.CHAT}
  //           element={
  //             <ProtectedRoute allow={isSuperAdmin}>
  //               <PChat />
  //             </ProtectedRoute>
  //           }
  //         />
  //         <Route path="*" element={<Navigate replace to={ROUTES.CHAT} />} />
  //       </Routes>
  //     </Router>
  //   )
  // }

  return (
    <Router>
      <Routes>
        <Route path={ROUTES.SIGN_IN_WITH_EMAIL_LINK} element={<Navigate replace to="/" />} />
        {/* <Route
          path={ROUTES.CHAT}
          element={
            <ProtectedRoute allow={isSuperAdmin}>
              <PChat />
            </ProtectedRoute>
          }
        /> */}
        <Route
          path={ROUTES.ADD_ADMIN_USER}
          element={
            <ProtectedRoute allow={isSuperAdmin}>
              <PAddAdminUser />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_USER}
          element={
            <ProtectedRoute allow={isSuperAdmin}>
              <PAdminUser />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_USERS}
          element={
            <ProtectedRoute allow={isSuperAdmin}>
              <PAdminUsers />
            </ProtectedRoute>
          }
        />

        <Route path={`${ROUTES.USER}/*`} element={<PUser />} />

        <Route path={ROUTES.USERS} element={<PUsers />} />
        <Route
          path={ROUTES.ADD_ORGANIZATION}
          element={
            <ProtectedRoute allow={isSuperAdmin}>
              <PAddOrganization />
            </ProtectedRoute>
          }
        />
        <Route path={ROUTES.ORGANIZATION} element={<POrganizationDetails />} />
        <Route
          path={ROUTES.ADD_TEAM}
          element={
            <ProtectedRoute allow={isSuperAdmin}>
              <PAddOrganizationTeam />
            </ProtectedRoute>
          }
        />
        <Route path={`${ROUTES.ORGANIZATION_TEAM}/*`} exact element={<POrganizationTeam />} />
        <Route path={`${ROUTES.TEAM_USER}/*`} element={<PUser />} />

        <Route
          path={ROUTES.ADD_TEAM_USER}
          element={
            <ProtectedRoute allow={isSuperAdmin}>
              <PAddTeamUser />
            </ProtectedRoute>
          }
        />
        <Route path={`${ROUTES.ASSESSMENTS}/*`} element={<PAssessments />} />
        <Route path={ROUTES.ROOT} exact element={<POrganizations />} />
      </Routes>
    </Router>
  )
}

const ProtectedRoute = ({ allow, children }) => {
  if (!allow) {
    return <Navigate to={ROUTES.ROOT} replace />
  }

  return children
}

export default App
